import { Injectable } from '@angular/core';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { ISearchResultItem } from '@app/core/model/search/search-result-item';
import { BsApiEndPoints } from '../bs-api/endpoints';
import { SiteNavigationLinksService } from '../site-navigation/site-navigation-links.service';
import { ISearchResourceConfig } from './search-resource-dictionary-types';
import IResource from '@app/core/model/resource';
import { IRun } from '@app/core/model/runs/run';
import { IBioSample } from '@app/core/model/bioSamples/bioSample';

export interface ISearchResourceDictionaryService {
  getResourceConfigByType(searchType: string): ISearchResourceConfig;
}
/**
 * Manages the search configuration for resource types
 */
@Injectable({
  providedIn: 'root'
})
export class SearchResourceDictionaryService {

  public project: ISearchResourceConfig;
  public run: ISearchResourceConfig;
  public app: ISearchResourceConfig;
  public biosample: ISearchResourceConfig;
  public sample: ISearchResourceConfig;
  public file: ISearchResourceConfig;
  public appresultfile: ISearchResourceConfig;
  public appSession: ISearchResourceConfig;
  public appResult: ISearchResourceConfig;
  public dataset: ISearchResourceConfig;
  public biosampleLibraryPrep: ISearchResourceConfig;

  constructor(private siteNavigationLinksService: SiteNavigationLinksService) {
    this.setResourceDictionaries();
  }

  /**
   * Gets the resource config by resource type
   * @param resourceType The Resource Type
   */
  public getResourceConfigByType(resourceType: SearchResourceType): ISearchResourceConfig {
    switch (resourceType) {
      case SearchResourceType.Project:
        return this.project;
      case SearchResourceType.Run:
        return this.run;
      case SearchResourceType.App:
        return this.app;
      case SearchResourceType.Biosample:
        return this.biosample;
      case SearchResourceType.Sample:
        return this.sample;
      case SearchResourceType.File:
        return this.file;
      case SearchResourceType.AppResultFile:
        return this.appresultfile;
      case SearchResourceType.AppSession:
        return this.appSession;
      case SearchResourceType.AppResult:
        return this.appResult;
      case SearchResourceType.Dataset:
        return this.dataset;
      case SearchResourceType.BiosampleLibraryPrep:
        return this.biosampleLibraryPrep;
      default:
        throw new Error('Unknown resource type ' + resourceType);
    }
  }

  public getSearchResultResource(searchResultItem: ISearchResultItem, resourceType: SearchResourceType): IResource {
    switch(resourceType) {
      case SearchResourceType.Run:
        return searchResultItem.Run;
      case SearchResourceType.Biosample:
        return searchResultItem.BioSample;
      case SearchResourceType.Project:
        return searchResultItem.Project;
      case SearchResourceType.App:
        return searchResultItem.Application;
      case SearchResourceType.File:
        return searchResultItem.DatasetFile;
      case SearchResourceType.AppResultFile:
        return searchResultItem.AppResultFile;
      case SearchResourceType.Sample:
        return searchResultItem.Sample;
      case SearchResourceType.AppResult:
        return searchResultItem.AppResult;
      case SearchResourceType.Dataset:
        return searchResultItem.Dataset;
      case SearchResourceType.BiosampleLibraryPrep:
        return searchResultItem.BioSampleLibraryPrep;
      default:
        throw new Error(`Have not implemented ${resourceType} yet`);  
    }
  }

  public getSearchResourceName(searchResourceItem: ISearchResultItem, resourceType: SearchResourceType): string {
    const resource = this.getSearchResultResource(searchResourceItem, resourceType);
    switch(resourceType) {
      case SearchResourceType.Run:
        return (resource as IRun).ExperimentName;
      case SearchResourceType.Biosample:
      case SearchResourceType.BiosampleLibraryPrep:
        return (resource as IBioSample).BioSampleName;
      default:
        return resource.Name;
    }
  }

  private setResourceDictionaries(): void {
    this.project = {
      key: 'project',
      displayNameSingular: 'Project',
      displayNamePlural: 'Projects',
      searchObjectName: 'Project',
      searchProperties: ['Name'],
      chooserMessage: 'Select Project(s)',
      idPropertyName: 'Id',
      namePropertyName: 'Name',
      hrefPropertyName: 'Href',
      searchScope: 'projects',
      apiBaseHrefFragment: 'projects',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.projectSummaryUrl(item.Project.Id),
      convertContentObject: (content: any) => {
        return { Project: content };
      },
      apiHrefAccessor: (item: ISearchResultItem) =>
        // TODO: Should this be prefixed with the 'v2'? Old basespace UI calls don't do this but they also seem to be broken...
         BsApiEndPoints.getProjectsPath(item.Project.Id),
      baseQuery: null
    };

    this.biosample = {
      key: 'biosample',
      displayNameSingular: 'Biosample',
      displayNamePlural: 'Biosamples',
      searchObjectName: 'Biosample',
      searchProperties: ['BioSampleName'],
      chooserMessage: 'Select Biosample(s)',
      idPropertyName: 'Id',
      namePropertyName: 'BioSampleName',
      hrefPropertyName: 'Href',
      searchScope: 'biosamples',
      apiBaseHrefFragment: 'biosamples',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.bioSampleSummaryUrl(item.BioSample.Id),
      convertContentObject: (content: any) => {
        return { BioSample: content };
      },
      apiHrefAccessor: (item: ISearchResultItem) =>
         `v2/${BsApiEndPoints.getBiosamplesPath(item.BioSample.Id)}`,
      baseQuery: null
    };

    this.biosampleLibraryPrep = {
      key: 'biosamplelibraryprep',
      displayNameSingular: 'Biosample Library Prep',
      displayNamePlural: 'Biosample Library Preps',
      searchObjectName: 'BioSampleLibraryPrep',
      searchProperties: ['BioSampleName'],
      chooserMessage: 'Select Biosample Library Prep(s)',
      idPropertyName: 'Id',
      searchScope: 'biosamplelibrarypreps',
      searchFilters: null,
      isCreatableInChooser: false,
      uiHrefAccessor: (item: ISearchResultItem) => {
        // Extract biosample ID from composite ID (format: "biosampleId::prepId")
        const biosampleId = item.BioSampleLibraryPrep.Id.split('::')[0];
        return this.siteNavigationLinksService.bioSampleSummaryUrl(biosampleId);
      },
      convertContentObject: (content: any) => {
        return { BioSampleLibraryPrep: content };
      },
      apiHrefAccessor: (item: ISearchResultItem) =>
         `v2/${BsApiEndPoints.getBiosamplesPath(item.BioSampleLibraryPrep.Id.split('::')[0])}`,
      baseQuery: null
    };

    this.run = {
      key: 'run',
      displayNameSingular: 'Run',
      displayNamePlural: 'Runs',
      searchObjectName: 'Run',
      searchProperties: ['ExperimentName', 'FlowcellBarcode'],
      chooserMessage: 'Select Run(s)',
      idPropertyName: 'Id',
      namePropertyName: 'Name',
      hrefPropertyName: 'Href',
      searchScope: 'runs',
      apiBaseHrefFragment: 'runs',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.runSummaryUrl(item.Run.Id),
    };

    this.app = {
      key: 'app',
      displayNameSingular: 'App',
      displayNamePlural: 'Apps',
      searchObjectName: 'Application',
      searchProperties: ['Name'],
      chooserMessage: 'Select App(s)',
      idPropertyName: 'Id',
      namePropertyName: 'Name',
      hrefPropertyName: 'Href',
      searchScope: 'apps',
      apiBaseHrefFragment: 'apps',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.appSummaryUrl(item.Application.Id),
    };

    this.biosample = {
      key: 'biosample',
      displayNameSingular: 'Biosample',
      displayNamePlural: 'Biosamples',
      searchObjectName: 'Biosample',
      searchProperties: ['BioSampleName'],
      chooserMessage: 'Select Biosample(s)',
      idPropertyName: 'Id',
      namePropertyName: 'BioSampleName',
      hrefPropertyName: 'Href',
      searchScope: 'biosamples',
      apiBaseHrefFragment: 'biosamples',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.bioSampleSummaryUrl(item.BioSample.Id),
      convertContentObject: (content: any) => {
        return { BioSample: content };
      },
      apiHrefAccessor: (item: ISearchResultItem) =>
         `v2/${BsApiEndPoints.getBiosamplesPath(item.BioSample.Id)}`,
      baseQuery: null
    };

    this.sample = {
      key: 'sample',
      displayNameSingular: 'Sample',
      displayNamePlural: 'Samples',
      searchObjectName: 'Sample',
      searchProperties: ['SampleId'],
      chooserMessage: 'Select Sample(s)',
      idPropertyName: 'Id',
      namePropertyName: 'Name',
      hrefPropertyName: 'Href',
      searchScope: 'samples',
      apiBaseHrefFragment: 'samples',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.sampleSummaryUrl(item.Sample.Id, item.Sample.Name),
      convertContentObject: (content: any) => {
        return { Sample: content };
      },
      apiHrefAccessor: (item: ISearchResultItem) => BsApiEndPoints.getSamplesPath(item.Sample.Id),
      baseQuery: 'Status:Complete'
    };

    this.file = {
      key: 'file',
      displayNameSingular: 'Dataset File',
      displayNamePlural: 'Dataset Files',
      searchObjectName: 'DatasetFile',
      searchProperties: ['Name'],
      chooserMessage: 'Select File(s)',
      idPropertyName: 'Id',
      namePropertyName: 'Name',
      hrefPropertyName: 'Href',
      searchScope: 'datasetfiles',
      apiBaseHrefFragment: 'files',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.fileSummaryUrl(item.DatasetFile.ParentDataset.AppSession.Id, item.DatasetFile.ParentDataset.Id),
    };

    this.appresultfile = {
        key: "appresult_file",
        displayNameSingular: "File",
        displayNamePlural: "Files",
        searchObjectName: "AppResultFile",
        searchProperties: ["Name"],
        chooserMessage: "Select File(s):",
        idPropertyName: "Id",
        namePropertyName: "Name",
        searchScope: "appresult_files",
        apiBaseHrefFragment: "files",
        isCreatableInChooser: false,
        uiHrefAccessor: (item: ISearchResultItem) => `/files/${item.AppResultFile.Id}`,
    }

    this.appSession = {
      key: 'appsession',
      displayNameSingular: 'Analysis',
      displayNamePlural: 'Analyses',
      searchObjectName: 'AppSession',
      searchProperties: ['Name'],
      chooserMessage: 'Select Analysis',
      idPropertyName: 'Id',
      namePropertyName: 'Name',
      searchScope: 'appsessions',
      apiBaseHrefFragment: 'appsessions',
      searchFilters: null,
      isCreatableInChooser: true,
      uiHrefAccessor: (item: ISearchResultItem) =>
        this.siteNavigationLinksService.fileSummaryUrl(item.DatasetFile.ParentDataset.AppSession.Id, item.DatasetFile.Id),
    };

    this.appResult = {
      key: 'appresult',
      displayNameSingular: 'App Result',
      displayNamePlural: 'App Results',
      searchObjectName: 'AppResult',
      searchProperties: ['Name'],
      chooserMessage: 'Select App Result(s)',
      idPropertyName: 'Id',
      namePropertyName: 'Name',
      searchScope: 'appresults',
      apiBaseHrefFragment: 'appresults',
    };

    this.dataset = {
      key: 'dataset',
      displayNameSingular: 'dataset',
      displayNamePlural: 'datasets',
      searchScope: 'datasets',
      searchObjectName: 'Dataset',
      searchProperties: ['Name'],
      chooserMessage: 'Select Dataset(s)',
      idPropertyName: 'Id',
      baseQuery: "-DatasetType.ConformsToIds:'common.fastq'"
    };

  }
}
